import { Button, Box } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarContainerProps,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import { adminDataGridSx } from '@/components/admin/adminDataGridStyles';
import { LocaleContext } from '@/Contexts/LocaleContext';
import useApplication from '@/hooks/useApplication';
import { ApplicationDataGridRowProps } from '@/models/Application';
import { GridLocaleTextOverride } from '@/utils/dataGridUtil';

import MassApprovalModal from './MassApprovalModal';
import { StatusColumnCell } from '@/components/common/StatusColumnCell';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { StatusProps } from '@/models/Status';
import { USCountryId } from '@/utils/constants';
import { programIdToProgramName } from '@/models/Program';

interface TcowToolbarProps extends GridToolbarContainerProps {
  selectedCount?: number;
  onApproveClick?: () => void;
}

const TcowMassApprovalToolbar = ({
  selectedCount = 0,
  onApproveClick,
  ...props
}: TcowToolbarProps) => (
  <GridToolbarContainer {...props}>
    <GridToolbarFilterButton />
    <Button variant='primary' size='small' disabled={selectedCount === 0} onClick={onApproveClick}>
      {`Approve Selected${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
    </Button>
    <div style={{ flex: 1 }} />
    <GridToolbarQuickFilter debounceMs={1500} />
  </GridToolbarContainer>
);

const columns = (
  allStatuses: StatusProps[],
  localeLanguageCode: string | null
): GridColDef<ApplicationDataGridRowProps>[] => [
  { field: 'applicationId', headerName: 'ID', flex: 0.5 },
  { field: 'partner', headerName: 'Organization', flex: 2 },
  {
    field: 'countryName',
    headerName: 'Location',
    flex: 1.25,
    renderCell: (params: GridRenderCellParams<ApplicationDataGridRowProps>) => {
      const countryName = params.value as string;

      if (params.row.countryId === USCountryId) {
        const stateName = params.row.stateName;

        return <span>{`${stateName} (US)`}</span>;
      }

      return <span>{countryName}</span>;
    },
  },
  {
    field: 'programId',
    headerName: 'Program',
    flex: 1,
    renderCell: (params: GridRenderCellParams<ApplicationDataGridRowProps>) => {
      const programId = params.value as number;

      return <span>{programIdToProgramName[programId]}</span>;
    },
  },
  { field: 'applicationCycle', headerName: 'Application Cycle', flex: 1 },
  StatusColumnCell(allStatuses, localeLanguageCode),
  {
    field: 'submissionDate',
    headerName: 'Submitted',
    flex: 1,
    valueGetter: (value: string) => {
      return value != null ? dayjs(value).format('LL') : '';
    },
  },
  { field: 'fullName', headerName: 'Contact', flex: 1.25 },
];

const TcowMassApproval: React.FC = () => {
  const { allStatuses } = useContext(ConfigContext);
  const { locale, localeLanguageCode } = useContext(LocaleContext);
  const { getApplicationsPendingApprovalAnnouncement } = useApplication();
  const [rows, setRows] = useState<ApplicationDataGridRowProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();

    getApplicationsPendingApprovalAnnouncement(abortController)
      .then((data) => {
        setRows(data ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    return () => abortController.abort();
  }, [getApplicationsPendingApprovalAnnouncement]);

  const selectedApplicationIds =
    selectedIds.type === 'include'
      ? Array.from(selectedIds.ids).map(Number)
      : rows.filter((r) => !selectedIds.ids.has(r.applicationId)).map((r) => r.applicationId);

  const selectedCount =
    selectedIds.type === 'include' ? selectedIds.ids.size : rows.length - selectedIds.ids.size;

  const handleApproveSuccess = (approvedIds: number[]) => {
    setRows((prev) => prev.filter((r) => !approvedIds.includes(r.applicationId)));
    setSelectedIds({ type: 'include', ids: new Set<GridRowId>() });
  };

  return (
    <>
      <Box sx={{ height: '65vh', width: '100%' }}>
        <DataGridPremium
          showToolbar
          loading={loading}
          localeText={GridLocaleTextOverride(locale)}
          columns={columns(allStatuses, localeLanguageCode)}
          rows={rows}
          getRowId={(row) => row.applicationId}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={setSelectedIds}
          initialState={{ density: 'compact' }}
          pagination
          slots={{ toolbar: TcowMassApprovalToolbar }}
          slotProps={{
            toolbar: {
              selectedCount,
              onApproveClick: () => setModalOpen(true),
            } as TcowToolbarProps,
          }}
          sx={adminDataGridSx}
        />
      </Box>

      <MassApprovalModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        applicationIds={selectedApplicationIds}
        onSuccess={handleApproveSuccess}
      />
    </>
  );
};

export default TcowMassApproval;
